/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Card, CircularProgress, Chip, IconButton, Tooltip,
  TableCell, TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel,
  Table, TableBody, TableContainer, TableRow, TablePagination,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../../../api';
import { useListUrlState } from '../../../../hooks/useListUrlState';
import { useListKeyboard, ACTIVE_ROW_SX } from 'hooks/useListKeyboard';

const TEAL = '#00798E';
const H = 45;
const TH = { py: 1.5, px: 3, textTransform: 'uppercase', fontSize: '0.625rem', fontWeight: 700, color: 'text.secondary', opacity: 0.7, borderBottom: '1.5px solid #f0f2f5', whiteSpace: 'nowrap', letterSpacing: '0.06em', bgcolor: 'transparent' };
const td = (last) => ({ py: 1.5, px: 3, borderBottom: last ? 'none' : '1px solid rgba(0,0,0,0.06)', fontSize: '0.8125rem' });
const SCHEDULE_COLOR = {
  H: 'warning', H1: 'error', X: 'error', G: 'warning',
  J: 'default', C: 'info', L: 'secondary', OTC: 'success', NONE: 'default',
};

export function ItemsPanel() {
  const navigate = useNavigate();
  const { readParams, writeParams } = useListUrlState();
  const urlParams = readParams();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(urlParams?.get('search') || '');
  const [categoryFilter, setCategoryFilter] = useState(urlParams?.get('category') || '');
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(urlParams ? Math.max(parseInt(urlParams.get('page'), 10) - 1, 0) || 0 : 0);
  const [pageSize, setPageSize] = useState((urlParams && parseInt(urlParams.get('limit'), 10)) || 20);

  const kb = useListKeyboard({
    rowCount: rows.length,
    onOpen: (i) => { if (rows[i]) navigate(`/pharmacy/mm/items/${rows[i].id}`); },
    onCreate: () => navigate('/pharmacy/mm/items/new'),
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      writeParams({ search, category: categoryFilter, page: page + 1, limit: pageSize });
      const res = await api.pharmacy.listItems({ search: search || undefined, irm_pharm_categories_id: categoryFilter || undefined, limit: pageSize, offset: page * pageSize });
      setRows(res.data || []); setTotal(res.total || 0);
    } catch { /* handled */ } finally { setLoading(false); }
  }, [search, categoryFilter, page, pageSize, writeParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    api.pharmacy.listCategories({ is_active: 'Y', limit: 200 }).then(r => setCategories(r.data || [])).catch(() => { });
  }, []);

  const handleDelete = async (row) => {
    const result = await Swal.fire({ title: 'Delete Item?', text: `"${row.name}" will be soft-deleted.`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Delete' });
    if (!result.isConfirmed) return;
    try { await api.pharmacy.deleteItem(row.id); fetchData(); } catch { /* handled */ }
  };

  return (
    <Card>
      <MDBox mx={2} mt={-3} py={2} px={2} variant="gradient" bgColor="logo" borderRadius="lg" coloredShadow="logo" display="flex" justifyContent="space-between" alignItems="center">
        <MDTypography variant="h6" color="white">Items / Materials</MDTypography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/pharmacy/mm/items/new')} sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>New Item (Alt+N)</Button>
      </MDBox>
      <MDBox p={2}>
        <MDBox display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
          <TextField {...kb.searchProps} size="small" placeholder="Search name, generic, code…  ( / to focus )" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} sx={{ width: 280, '& .MuiInputBase-root': { height: H } }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Category</InputLabel>
            <Select label="Category" value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(0); }} sx={{ height: H }}>
              <MenuItem value="">All Categories</MenuItem>
              {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
        </MDBox>
        <TableContainer sx={{ boxShadow: 'none' }}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={TH}>Code</TableCell>
                <TableCell sx={TH}>Item Name</TableCell>
                <TableCell sx={TH}>Generic Name</TableCell>
                <TableCell sx={TH}>Category</TableCell>
                <TableCell sx={TH}>Schedule</TableCell>
                <TableCell sx={TH}>Base Unit</TableCell>
                <TableCell sx={TH}>MRP</TableCell>
                <TableCell sx={TH}>Manufacturer</TableCell>
                <TableCell sx={TH}>Status</TableCell>
                <TableCell sx={TH}>Actions</TableCell>
              </TableRow>
              {loading ? (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, border: 'none' }}><CircularProgress size={24} sx={{ color: TEAL }} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, border: 'none', color: 'text.secondary' }}>No items found</TableCell></TableRow>
              ) : rows.map((row, idx) => {
                const last = idx === rows.length - 1;
                return (
                  <TableRow key={row.id} hover {...kb.getRowProps(idx)} sx={{ '&:hover': { bgcolor: 'rgba(0,121,142,0.04)' }, ...(kb.activeIdx === idx ? ACTIVE_ROW_SX : {}) }}>
                    <TableCell sx={td(last)}><MDTypography variant="caption" color="text" fontWeight="regular" sx={{ fontFamily: 'monospace' }}>{row.item_code}</MDTypography></TableCell>
                    <TableCell sx={td(last)}><MDTypography variant="caption" color="text" fontWeight="medium">{row.name}</MDTypography></TableCell>
                    <TableCell sx={td(last)}><MDTypography variant="caption" color="text" fontWeight="regular">{row.generic_name || '—'}</MDTypography></TableCell>
                    <TableCell sx={td(last)}><MDTypography variant="caption" color="text" fontWeight="regular">{row.category?.name || '—'}</MDTypography></TableCell>
                    <TableCell sx={td(last)}>
                      {row.drug_schedule && row.drug_schedule !== 'NONE'
                        ? <Chip label={`Sch-${row.drug_schedule}`} size="small" color={SCHEDULE_COLOR[row.drug_schedule] || 'default'} />
                        : <Chip label="OTC" size="small" color="success" />}
                    </TableCell>
                    <TableCell sx={td(last)}><MDTypography variant="caption" color="text" fontWeight="regular">{row.base_unit?.name || '—'}</MDTypography></TableCell>
                    <TableCell sx={td(last)}>
                      <MDTypography variant="caption" color="text" fontWeight="regular">
                        {row.mrp_per_base_unit !== null && row.mrp_per_base_unit !== undefined ? `₹${parseFloat(row.mrp_per_base_unit).toFixed(2)}` : '—'}
                      </MDTypography>
                    </TableCell>
                    <TableCell sx={td(last)}><MDTypography variant="caption" color="text" fontWeight="regular">{row.manufacturer?.name || '—'}</MDTypography></TableCell>
                    <TableCell sx={td(last)}>
                      <Chip label={row.is_active === 'Y' ? 'Active' : 'Inactive'} size="small" color={row.is_active === 'Y' ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell sx={td(last)}>
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="View / Edit"><IconButton size="small" onClick={() => navigate(`/pharmacy/mm/items/${row.id}`)}><VisibilityIcon fontSize="small" sx={{ color: TEAL }} /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDelete(row)}><DeleteIcon fontSize="small" color="error" /></IconButton></Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={pageSize} onRowsPerPageChange={e => { setPageSize(+e.target.value); setPage(0); }} rowsPerPageOptions={[10, 20, 50]} />
      </MDBox>
    </Card>
  );
}

export default function PharmItems() {
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}><ItemsPanel /></MDBox>
      <Footer />
    </DashboardLayout>
  );
}
